import type { Equipment, SafetyDevice, Instrument } from "../../equipment/equipment-domain.js";
import { formatReportValue, NOT_INFORMED } from "../../shared/format/report-value.js";

/**
 * LISTA DE LINHAS GERADA AUTOMATICAMENTE (Book de Inspeção de SISTEMA DE
 * TUBULAÇÃO).
 *
 * Fonte única e oficial: o CADASTRO DE EQUIPAMENTOS. O vínculo é o campo real
 * `Equipment.pipingSystemId` (Linha → Sistema) — nunca prefixo de TAG, nome de
 * arquivo, substring ou lista fixa. A operação é de leitura/consolidação: não
 * cria, não altera e não recalcula nada do cadastro técnico.
 *
 * As células já saem formatadas pelo formatter documental central, de modo que
 * Preview e PDF consomem exatamente o mesmo modelo (PREVIEW = PDF) e o
 * snapshot congela o que foi apresentado.
 */

export interface GeneratedLineListEntry {
  equipmentId: string;
  tag: string;
  systemTag: string;
  nominalDiameter: string;
  schedule: string;
  spec: string;
  /** FLUIDO — campo real da Linha (`Equipment.fluid`), nunca inferido pela TAG nem pelo Serviço. */
  fluid: string;
  /** SERVIÇO — campo real da Linha (`Equipment.service`), distinto de FLUIDO mesmo quando ambos coincidem no cadastro. */
  service: string;
  from: string;
  to: string;
  fluidClass: string;
  nr13Category: string;
  designPressure: string;
  operatingPressure: string;
  designTemperature: string;
  operatingTemperature: string;
  mawp: string;
  nominalThickness: string;
  minimumThickness: string;
  material: string;
  pressureClass: string;
  /** FLUXOGRAMA (P&ID/PFD) — própria da Linha (`Equipment.flowDiagramReference`, com fallback ao legado combinado). */
  flowDiagramReference: string;
  /** DESENHO DE REFERÊNCIA — próprio da Linha (`Equipment.drawingReference`, com fallback ao legado combinado). */
  drawingReference: string;
  /** PSVs / Válvulas de alívio associadas à linha (agregadas em multilinha '\n' sem duplicar linhas) */
  psv?: string | undefined;
  /** Instrumentos associados à linha (agregados em multilinha '\n' sem duplicar linhas) */
  instruments?: string | undefined;
}

export interface GeneratedLineList {
  systemId: string;
  systemTag: string;
  systemDescription: string;
  totalLines: number;
  generatedAt: string;
  lines: GeneratedLineListEntry[];
  /** Referências documentais das Linhas consolidadas no Sistema, sem duplicidade. */
  documentReferences: string[];
}

/**
 * Colunas da Lista COMPLETA (Book de Inspeção / PDF independente), na ordem
 * oficial — usadas pelo Preview e pelo PDF. FLUIDO e SERVIÇO são colunas
 * distintas: nunca combinadas numa só. Não inclui ISOMÉTRICO — não existe
 * campo oficial correspondente no cadastro do Equipment (não inventar
 * campo). Cabeçalhos NUNCA abreviados automaticamente (comando "autoajuste
 * inteligente", item 1): "DIÂMETRO NOMINAL", "PRESSÃO DE PROJETO/OPERAÇÃO",
 * "TEMPERATURA DE PROJETO/OPERAÇÃO", "CATEGORIA NR-13", "ESPESSURA
 * NOMINAL/MÍNIMA", "DESENHO DE REFERÊNCIA" ficam por extenso — SCH, SPEC e
 * PMTA são os próprios termos técnicos oficiais (não abreviações nossas de
 * um nome maior), preservados como tal.
 *
 * SEM largura fixa: a largura de cada coluna é calculada em tempo real por
 * `computeLineListLayout()` (`line-list-layout.ts`), a partir da largura
 * REAL medida (métrica Roboto, `text-metrics.ts`) do maior token que a
 * coluna precisa comportar em CADA documento gerado — nunca um número
 * mantido à mão. Isso significa que adicionar uma nova coluna aqui já basta
 * para o autoajuste passar a considerá-la, sem editar nenhum outro arquivo
 * (comando "autoajuste inteligente", item 18).
 */
export const LINE_LIST_COLUMNS: ReadonlyArray<{ key: keyof GeneratedLineListEntry; label: string }> = [
  { key: "tag", label: "TAG DA LINHA" },
  { key: "systemTag", label: "TAG DO SISTEMA" },
  { key: "fluid", label: "FLUIDO" },
  { key: "service", label: "SERVIÇO" },
  { key: "nominalDiameter", label: "DIÂMETRO NOMINAL" },
  { key: "schedule", label: "SCH" },
  { key: "spec", label: "SPEC" },
  { key: "from", label: "DE" },
  { key: "to", label: "PARA" },
  { key: "fluidClass", label: "CLASSE DE FLUIDO" },
  { key: "nr13Category", label: "CATEGORIA NR-13" },
  { key: "designPressure", label: "PRESSÃO DE PROJETO" },
  { key: "operatingPressure", label: "PRESSÃO DE OPERAÇÃO" },
  { key: "designTemperature", label: "TEMPERATURA DE PROJETO" },
  { key: "operatingTemperature", label: "TEMPERATURA DE OPERAÇÃO" },
  { key: "mawp", label: "PMTA" },
  { key: "nominalThickness", label: "ESPESSURA NOMINAL" },
  { key: "minimumThickness", label: "ESPESSURA MÍNIMA" },
  { key: "material", label: "MATERIAL" },
  { key: "pressureClass", label: "CLASSE DE PRESSÃO" },
  { key: "drawingReference", label: "DESENHO DE REFERÊNCIA" },
  { key: "flowDiagramReference", label: "FLUXOGRAMA" },
  { key: "psv", label: "PSV" },
  { key: "instruments", label: "INSTRUMENTOS" }
];

/**
 * Ordenação alfanumérica natural por TAG: 001, 002, 010, 020 — nunca a ordem
 * lexicográfica que colocaria 010 antes de 002.
 */
export function compareLineTags(a: string, b: string): number {
  return String(a || "").localeCompare(String(b || ""), "pt-BR", { numeric: true, sensitivity: "base" });
}

/** Linhas ATIVAS vinculadas ao Sistema, no mesmo Cliente — o filtro oficial. */
export function linesOfSystem(system: Pick<Equipment, "id" | "clientId">, equipments: readonly Equipment[]): Equipment[] {
  const systemId = String(system.id);
  const clientId = String(system.clientId);
  return equipments
    .filter(equipment => String(equipment.pipingSystemId || "") === systemId)
    .filter(equipment => String(equipment.clientId) === clientId)
    .filter(equipment => equipment.status === "ACTIVE" && !equipment.deletedAt)
    .sort((a, b) => compareLineTags(a.tag || a.lineNumber || "", b.tag || b.lineNumber || ""));
}

function pmtaOf(line: Equipment): string {
  if (line.pmtaApplicability === "notApplicable") return "NÃO APLICÁVEL";
  return formatReportValue({ value: line.pmtaValue ?? null, unit: line.pmtaUnit ?? null });
}

function firstInformed(...values: unknown[]): string {
  for (const value of values) {
    const formatted = formatReportValue(value);
    if (formatted !== NOT_INFORMED) return formatted;
  }
  return NOT_INFORMED;
}

function entryOf(
  line: Equipment,
  systemTag: string,
  safetyDevices?: readonly SafetyDevice[],
  instruments?: readonly Instrument[]
): GeneratedLineListEntry {
  const lineId = String(line.id);
  const psvs = safetyDevices
    ? safetyDevices
        .filter(d => (String(d.protectedLineId || "") === lineId || String(d.protectedEquipmentId || "") === lineId) && d.status === "ACTIVE")
        .map(d => d.tag)
        .filter(Boolean)
    : [];
  const insts = instruments
    ? instruments
        .filter(i => (String(i.pipingLineId || "") === lineId || String(i.equipmentId || "") === lineId) && i.status === "ACTIVE")
        .map(i => i.tag)
        .filter(Boolean)
    : [];

  // FLUXOGRAMA e DESENHO são campos próprios da Linha, cada um com sua
  // própria referência (comando complementar, item 15/34) — o campo legado
  // combinado só serve de fallback para cadastros anteriores à separação.
  return {
    equipmentId: String(line.id),
    tag: firstInformed(line.tag, line.lineNumber),
    systemTag: formatReportValue(systemTag),
    nominalDiameter: firstInformed(line.nps),
    schedule: formatReportValue(line.schedule),
    spec: formatReportValue(line.spec),
    fluid: formatReportValue(line.fluid),
    service: formatReportValue(line.service),
    from: firstInformed(line.fromReference),
    to: firstInformed(line.toReference),
    fluidClass: formatReportValue(line.fluidClass),
    nr13Category: line.nr13Applicability === "NOT_APPLICABLE" ? "NÃO APLICÁVEL" : formatReportValue(line.nr13Category),
    designPressure: formatReportValue(line.designPressure),
    operatingPressure: formatReportValue(line.operatingPressure),
    designTemperature: formatReportValue(line.designTemperature),
    operatingTemperature: formatReportValue(line.operatingTemperature),
    mawp: pmtaOf(line),
    nominalThickness: formatReportValue(line.nominalThickness),
    minimumThickness: formatReportValue(line.minimumRequiredThickness),
    material: firstInformed(line.material, line.materialSpecification),
    pressureClass: formatReportValue(line.pressureClass),
    flowDiagramReference: formatReportValue(line.flowDiagramReference ?? line.projectDrawingFlowchart),
    drawingReference: formatReportValue(line.drawingReference ?? line.projectDrawingFlowchart),
    psv: psvs.length > 0 ? psvs.join("\n") : undefined,
    instruments: insts.length > 0 ? insts.join("\n") : undefined
  };
}

/**
 * Referências documentais das Linhas consolidadas no Sistema, sem duplicidade
 * e na ordem natural. Referência documental NÃO é anexo: aqui só se consolida
 * o texto cadastrado na Linha, nunca se afirma que existe arquivo.
 */
export function collectSystemDocumentReferences(lines: readonly GeneratedLineListEntry[]): string[] {
  const references = new Set<string>();
  for (const line of lines) {
    for (const reference of [line.flowDiagramReference, line.drawingReference]) {
      if (reference && reference !== NOT_INFORMED && reference !== "NÃO APLICÁVEL") references.add(reference);
    }
  }
  return [...references].sort((a, b) => compareLineTags(a, b));
}

/**
 * Monta a Lista de Linhas do Sistema. `totalLines` é sempre a contagem real
 * das Linhas vinculadas — nunca um número declarado manualmente.
 */
export function buildGeneratedLineList(
  system: Equipment,
  equipments: readonly Equipment[],
  generatedAt?: string,
  safetyDevices?: readonly SafetyDevice[],
  instruments?: readonly Instrument[]
): GeneratedLineList;
export function buildGeneratedLineList(
  system: Equipment,
  equipments: readonly Equipment[],
  safetyDevices?: readonly SafetyDevice[],
  instruments?: readonly Instrument[]
): GeneratedLineList;
export function buildGeneratedLineList(
  system: Equipment,
  equipments: readonly Equipment[],
  generatedAtOrSafetyDevices?: string | readonly SafetyDevice[],
  safetyDevicesOrInstruments?: readonly SafetyDevice[] | readonly Instrument[],
  instruments?: readonly Instrument[]
): GeneratedLineList {
  let generatedAt = new Date().toISOString();
  let safetyDevices: readonly SafetyDevice[] | undefined;
  let insts: readonly Instrument[] | undefined;

  if (typeof generatedAtOrSafetyDevices === "string") {
    generatedAt = generatedAtOrSafetyDevices;
    safetyDevices = safetyDevicesOrInstruments as readonly SafetyDevice[] | undefined;
    insts = instruments;
  } else if (Array.isArray(generatedAtOrSafetyDevices)) {
    safetyDevices = generatedAtOrSafetyDevices;
    insts = safetyDevicesOrInstruments as readonly Instrument[] | undefined;
  }

  const lines = linesOfSystem(system, equipments).map(line => entryOf(line, system.tag || "", safetyDevices, insts));
  return {
    systemId: String(system.id),
    systemTag: system.tag || "",
    systemDescription: system.name || system.description || "",
    totalLines: lines.length,
    generatedAt,
    lines,
    documentReferences: collectSystemDocumentReferences(lines)
  };
}
